/** Needs-you queue (screens §F1): GxQueueItem per kind, in the table's row order. Items that need more than a click open a drawer. */
import { Link } from "../shell/history";
import { Icon } from "../theme/icons";
import { GxCard, GxEmptyState } from "../theme/primitives";
import { GxPill } from "../theme/GxPill";
import { precheckCounts, precheckWords } from "./derive";
import { SIGNED_OVER } from "./words";
import { SeatPin } from "./SeatPin";
import type { Act } from "./useAct";
import type { QueueItem } from "./queue";

export const REQUEST_KINDS: Readonly<Record<string, string>> = { mou_dividend: "MOU dividend", late_submission: "more time", deadline_extension: "more time", other: "other" };
export const MORE_TIME_KINDS: ReadonlySet<string> = new Set(["late_submission", "deadline_extension"]);

const tone = (key: string): string => `gx-fac-qk gx-fac-qk--${key}`;

export function NeedsYou({ queue, cohortId, lowEngagementShare, onOpen, onSwap, onDrop, onDismiss, cardTitle = () => "A surprise", calledIn = [], act }: {
  /** With it, "Give a new PIN" opens the PIN dialog here; without it the button is the Roster link. */
  readonly act?: Act;
  /** People called to the pre-check this session. */
  readonly calledIn?: readonly string[];
  readonly queue: readonly QueueItem[]; readonly cohortId: string; readonly lowEngagementShare: number;
  readonly onOpen: (item: QueueItem) => void; readonly onSwap: () => void; readonly onDrop: () => void; readonly onDismiss: (key: string) => void;
  /** The card's own title for its id (no ids in the words); "A surprise" when the list is not known. */
  readonly cardTitle?: (eventId: string) => string;
}) {
  const open = (item: QueueItem, label: string) => <button type="button" className="gx-btn gx-btn--secondary" onClick={() => onOpen(item)}>{label}</button>;
  return (
    <GxCard testId="board.queue" className="gx-fac-queue" title="Needs you" aside={<GxPill tone="neutral">{queue.length} open</GxPill>}>
      {queue.length === 0 ? <GxEmptyState title="Nothing needs you. Teams are working." /> : null}
      {queue.map((item) => (
        <div key={item.key} className={`gx-fac-qi${item.kind === "surprise" || item.kind === "plannedSurprise" ? " gx-fac-qi--hot" : ""}`} data-testid={`queue.${item.kind}`}>
          {item.kind === "roles" ? <>
            <div className={tone("crit")}>Roles missing</div>
            <div className="gx-fac-qt">{item.team.name} needs a {item.team.missingRoles.map((r) => r.toUpperCase()).join(" and ")}</div>
            <div className="gx-fac-qd">The round cannot open until every team has a CEO and a risk officer.</div>
            <div className="gx-fac-qa"><Link className="gx-btn gx-btn--secondary" href={`/f/${cohortId}/roster`}>Open Roster</Link></div>
          </> : null}
          {item.kind === "strategy" ? <>
            <div className={tone("crit")}>Strategy not set</div>
            <div className="gx-fac-qt">{item.team.name}: strategy not set</div>
            <div className="gx-fac-qd">Round 1 cannot open until its CEO has written and saved the strategy statement.</div>
          </> : null}
          {item.kind === "seat" ? <>
            <div className={tone("warn")}>Risk officer is not online</div>
            <div className="gx-fac-qt">{item.team.name}'s risk officer is not online. The CEO can sign without a risk review if the risk officer has never signed in; the sign box on the team's screen says which.</div>
            <div className="gx-fac-qa">{act === undefined ? <Link className="gx-btn gx-btn--secondary" data-testid="queue.seat.pin" href={`/f/${cohortId}/roster`}>Give a new PIN</Link> : <SeatPin cohortId={cohortId} team={item.team} role="cro" act={act} label="Give a new PIN" className="gx-btn gx-btn--secondary" testId="queue.seat.pin" />}<Link className="gx-btn gx-btn--ghost" data-testid="queue.seat.roster" href={`/f/${cohortId}/roster`}>Open Roster</Link></div>
          </> : null}
          {item.kind === "overrode" ? <>
            <div className={tone("warn")}>Signed over the risk officer</div>
            <div className="gx-fac-qt">{item.team.name}: the CEO {SIGNED_OVER}</div>
          </> : null}
          {item.kind === "rationales" ? <>
            <div className={tone("warn")}>Reasons missing</div>
            <div className="gx-fac-qt">{item.team.name} is missing {item.team.missingRationales} {item.team.missingRationales === 1 ? "reason" : "reasons"} for its changes</div>
          </> : null}
          {item.kind === "surprise" ? <>
            <div className={tone("gold")}><Icon name="bolt" size={13} />{item.releasable ? "Surprise ready to release" : "Surprise held"}</div>
            <div className="gx-fac-qt">{cardTitle(item.surprise.eventId)} for {item.surprise.teamIds.length} {item.surprise.teamIds.length === 1 ? "team" : "teams"}, at month {item.surprise.month}</div>
            <div className="gx-fac-qd">{item.releasable ? "Teams see it when you release it from the band." : "Teams never see it before you close the round. You release it from the band."}</div>
            <div className="gx-fac-qa">{open(item, "Preview")}<button type="button" className="gx-btn gx-btn--ghost" onClick={onSwap}>Swap card</button><button type="button" className="gx-btn gx-btn--ghost" onClick={onDrop}>Drop</button></div>
          </> : null}
          {item.kind === "plannedSurprise" ? <>
            <div className={tone("gold")}><Icon name="bolt" size={13} />Planned surprise</div>
            <div className="gx-fac-qt">{cardTitle(item.planned.eventId)}, planned for round {item.planned.roundNo}, month {item.planned.month}</div>
            <div className="gx-fac-qd">You can hold it once round {item.planned.roundNo} is open. Any other card can be held now from the round menu (the three dots on the band).</div>
          </> : null}
          {item.kind === "lowEngagement" ? <>
            <div className={tone("warn")}>Low engagement</div>
            <div className="gx-fac-qt">{item.team.name} has changed {item.team.changedFamilies} {item.team.changedFamilies === 1 ? "decision area" : "decision areas"} at {Math.round(lowEngagementShare * 100)}% of the countdown</div>
            <div className="gx-fac-qa"><button type="button" className="gx-btn gx-btn--ghost" onClick={() => onDismiss(item.key)}>Dismiss</button></div>
          </> : null}
          {item.kind === "request" ? <>
            <div className={tone("info")}>Request · {REQUEST_KINDS[item.exception.kind] ?? item.exception.kind}</div>
            <div className="gx-fac-qt">{item.teamName}: "{item.exception.justification}"</div>
            <div className="gx-fac-qa">{open(item, "Review request")}</div>
          </> : null}
          {item.kind === "narrative" ? <>
            <div className={tone("ink")}>Held narrative</div>
            <div className="gx-fac-qt">{item.count} {item.count === 1 ? "narrative is" : "narratives are"} waiting for your review</div>
            <div className="gx-fac-qa">{open(item, "Read and decide")}</div>
          </> : null}
          {item.kind === "coach" ? <>
            <div className={tone("ink")}><Icon name="coach" size={13} />Coach signal</div>
            <div className="gx-fac-qt">{item.turns.length} flagged coach {item.turns.length === 1 ? "turn" : "turns"}</div>
            <div className="gx-fac-qa">{open(item, "Open coach log")}</div>
          </> : null}
          {item.kind === "precheck" ? <>
            <div className={tone("info")}>Pre-check catch-up</div>
            <div className="gx-fac-qt" data-testid="queue.precheck.counts">{precheckWords(precheckCounts(item.userIds, item.deferredIds, calledIn, item.roundInPlay), item.roundInPlay)}</div>
            <div className="gx-fac-qa">{open(item, "See who")}</div>
          </> : null}
          {item.kind === "picks" ? <>
            <div className={tone("gold")}>Picks ready</div>
            <div className="gx-fac-qt">Round {item.roundNo} debrief picks are ready: open Studio to show them</div>
            <div className="gx-fac-qa"><Link className="gx-btn gx-btn--secondary" href={`/f/${cohortId}/studio`}>Go to Studio</Link></div>
          </> : null}
        </div>
      ))}
    </GxCard>
  );
}

